import { useState } from 'react';
import { Button, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PRZEŁĄCZNIK PANELI — jedno kliknięcie między trzema systemami TuSzamy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ TO SĄ ZWYKŁE ODNOŚNIKI I TAK MA ZOSTAĆ ────────────────────────────────────────────────────┐
 * │ Przełącznik NIE przenosi sesji, tokenu ani niczego, co uwierzytelnia. Każdy panel ma własne │
 * │ logowanie i własną sesję.                                                                    │
 * │                                                                                              │
 * │ Wygodne „przelogowanie jednym kliknięciem" wymagałoby przekazania poświadczenia przez adres  │
 * │ albo przez wspólne ciasteczko na domenie nadrzędnej. Pierwsze zostawia token w historii      │
 * │ przeglądarki, w nagłówku `Referer` i w dziennikach serwerów pośredniczących; drugie          │
 * │ rozszerza zasięg sesji na wszystkie poddomeny naraz. Za jedno kliknięcie mniej płaci się     │
 * │ wtedy zakresem, którego nie da się cofnąć.                                                    │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ADRESY SĄ ZMIERZONE, NIE ZAŁOŻONE (2026-08-26, tytuły odczytane z żywych odpowiedzi):
 *
 *   Control Center   https://panel.tuszama.pl             „TuSzama Control Center"
 *   CRM              https://kierowcy.tuszama.pl/crm/     „TuSzama — CRM"
 *   TuSzama Drive    https://kierowcy.tuszama.pl/admin    „TuSzama — Panel Administracyjny"
 *
 * ┌─ TRZY PUŁAPKI, W KTÓRE ŁATWO WPAŚĆ MIERZĄC SAMYM KODEM ODPOWIEDZI ──────────────────────────┐
 * │ 1. `crm.tuszama.pl` NIE ODPOWIADA. Nazwa krąży po dokumentacji, ale nie ma za nią usługi.   │
 * │                                                                                              │
 * │ 2. `panel.tuszama.pl/crm/` zwraca 200 — i jest zastępczą stroną Control Center, nie CRM-em. │
 * │    Adres CRM potwierdza `crm-prod-deploy.yml`, który po wdrożeniu weryfikuje dokładnie      │
 * │    `kierowcy.tuszama.pl/crm/`.                                                               │
 * │                                                                                              │
 * │ 3. KORZEŃ `kierowcy.tuszama.pl` TO APLIKACJA KIEROWCY, nie panel administracyjny —          │
 * │    tytuł „TuSzama — Aplikacja Kierowcy". Pierwsza wersja tego przełącznika prowadziła       │
 * │    właśnie tam, bo adres odpowiadał dwusetką. Panel administracyjny stoi pod `/admin`       │
 * │    (`admin.html`); że to trasa produkcyjna, a nie przypadek, potwierdzają komentarze        │
 * │    w konfiguracji CRM wymieniające powierzchnie Drive jako `(/, /admin, /api/)`.            │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */

interface Panel {
  id: string;
  nazwa: string;
  opis: string;
  adres: string;
  /** `true` dla panelu, w którym właśnie jesteśmy — nie prowadzimy do samego siebie. */
  biezacy?: boolean;
}

const PANELE: Panel[] = [
  {
    id: 'control-center',
    nazwa: 'Control Center',
    opis: 'Konsola operacyjna — tu jesteś',
    adres: 'https://panel.tuszama.pl',
    biezacy: true,
  },
  {
    id: 'crm',
    nazwa: 'CRM',
    opis: 'Klienci, kampanie, analityka sprzedaży',
    adres: 'https://kierowcy.tuszama.pl/crm/',
  },
  {
    id: 'drive',
    nazwa: 'TuSzama Drive',
    opis: 'Panel administracyjny — dostawy i kierowcy',
    adres: 'https://kierowcy.tuszama.pl/admin',
  },
];

export function PrzelacznikPaneli() {
  const [kotwica, setKotwica] = useState<null | HTMLElement>(null);
  const otwarte = Boolean(kotwica);

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<AppsIcon />}
        onClick={(e) => setKotwica(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={otwarte}
        aria-label="Przełącz panel"
        sx={{ flex: 'none' }}
      >
        Panele
      </Button>

      <Menu
        anchorEl={kotwica}
        open={otwarte}
        onClose={() => setKotwica(null)}
        slotProps={{ list: { 'aria-label': 'Panele TuSzamy' } }}
      >
        {PANELE.map((p) => (
          <MenuItem
            key={p.id}
            component={p.biezacy ? 'div' : 'a'}
            href={p.biezacy ? undefined : p.adres}
            // Nowa karta, ale BEZ dostępu do okna źródłowego: `noopener` odcina `window.opener`,
            // przez które strona docelowa mogłaby przekierować kartę, z której wyszła.
            target={p.biezacy ? undefined : '_blank'}
            rel={p.biezacy ? undefined : 'noopener noreferrer'}
            disabled={p.biezacy}
            onClick={() => setKotwica(null)}
            sx={{ gap: 1.5 }}
          >
            <ListItemText
              primary={p.nazwa}
              secondary={p.opis}
              slotProps={{ secondary: { variant: 'caption' } }}
            />
            {!p.biezacy && <OpenInNewIcon fontSize="small" sx={{ opacity: 0.6 }} />}
          </MenuItem>
        ))}

        <Typography
          variant="caption"
          component="p"
          sx={{ px: 2, pt: 1, pb: 0.5, maxWidth: 260, color: 'text.secondary' }}
        >
          Każdy panel ma własne logowanie — sesja nie jest przenoszona między nimi.
        </Typography>
      </Menu>
    </>
  );
}

export { PANELE };
